import { createContext, useCallback, useEffect, useState, ReactNode, useContext, useMemo } from 'react';
import { setAuthorizationHeader } from 'src/api';
import { PrescriptionViewer } from 'src/components/PrescriptionViewer';
import { PrescriptionPreviewModalWrapper } from 'src/components/PrescriptionPreviewModalWrapper';
import { DEFAULT_CREATE_PRESCRIBER_FORM_DATA } from 'src/helpers/defaultValues';
import { encryptToMd5 } from 'src/helpers/string';
import useLoading from 'src/helpers/useLoading';
import { AuthService, USER_DOES_NOT_HAVE_SESSION } from 'src/services/AuthService';
import { Patient } from 'src/services/patient';
import {
  CreatePrescriberPayload,
  PRESCRIBER_STATUS,
  Prescriber,
  PrescriberService,
  PRESCRIPTION_ITEM_ICON,
} from 'src/services/prescriber';
import { Tracker } from 'src/shared/tracker/Tracker';
import { DialogWrappedComponentProps, useFeedbackContext } from './FeedbackProvider';
import { isRunningInWebview } from 'src/helpers/isRunningInWebview';
import { appFailureSignedIn, safeGetFromLocalStorage } from 'src/helpers/window';
import { BOTTOM_TAB_PADDING_BOTTOM } from 'src/components/Layout/BottomTabNavigation/BottomTabNavigation';
import { useAuthContext } from './AuthProvider';
import { get } from 'lodash';
import { useAssistantContext } from './AssistantProvider';
import { AssistantData, AssistantService } from 'src/services/AssistantService';
import { useLocation } from 'react-router-dom';

export const NOT_OPEN_BOTTOM_BAR_HEIGHT = 88 + BOTTOM_TAB_PADDING_BOTTOM;
export const OPEN_BOTTOM_BAR_HEIGHT = 180 + BOTTOM_TAB_PADDING_BOTTOM;

const B2C_PARTNER_ID = 331;

export enum USER_GROUPS {
  prescriber = 'prescriber',
  assistant = 'assistant',
  not_validated = 'not_validated',
}

interface PrescriberContextProps {
  updateCreatePrescriberFormData(data: Partial<CreatePrescriberPayload>): void;
  createPrescriberFormData: CreatePrescriberPayload;
  prescriberId: string | null;
  setPrescriberId(prescriberId: string | null): void;
  prescriberData: Prescriber | null;
  setPrescriberData(prescriberData: Prescriber | null): void;
  retrievePrescriberData(): Promise<void>;
  startPrescrition(patientId: string): Promise<void>;
  loadingPrescription: boolean;
  patients: Patient[] | null;
  setPatients(data: Patient[] | null): void;
  loadingPrescriberData: boolean;
  isUserOnApp: boolean;
  bottomBarHeight: number;
  setBottomBarHeight(value: number): void;
  clickOnFirstPatientFromList(): void;
  userDoesNotHaveAnyPatients: boolean;
  setUserDoesNotHaveAnyPatients(value: boolean): void;
  prescriberStatus: PRESCRIBER_STATUS | null;
  isUserInvalid: boolean;
  getAndSetPrescriberStatus(prescriberId: string): Promise<void>;
  userGroup: USER_GROUPS;
}

export const PrescriberContext = createContext<PrescriberContextProps>({
  updateCreatePrescriberFormData: (data: Partial<CreatePrescriberPayload>) => {},
  createPrescriberFormData: DEFAULT_CREATE_PRESCRIBER_FORM_DATA(),
  prescriberId: null,
  setPrescriberId: (prescriberId: string | null) => null,
  prescriberData: null,
  setPrescriberData: (prescriberData: Prescriber | null) => null,
  retrievePrescriberData: async () => {},
  startPrescrition: async (patientId: string) => {},
  loadingPrescription: false,
  patients: null,
  setPatients: (data: Patient[] | null) => {},
  loadingPrescriberData: false,
  isUserOnApp: false,
  bottomBarHeight: NOT_OPEN_BOTTOM_BAR_HEIGHT,
  setBottomBarHeight: (value: number) => {},
  clickOnFirstPatientFromList: () => {},
  userDoesNotHaveAnyPatients: false,
  setUserDoesNotHaveAnyPatients: (value: boolean) => {},
  prescriberStatus: null,
  isUserInvalid: false,
  getAndSetPrescriberStatus: async (prescriberId: string) => {},
  userGroup: USER_GROUPS.not_validated,
});

export interface PrescriberProviderInterface {
  children: ReactNode;
}

export function PrescriberProvider(props: PrescriberProviderInterface) {
  const [prescriberId, setPrescriberId] = useState<string | null>(null);

  const [loadingPrescriberData, runWithLoadingPrescriberData] = useLoading();
  const [isUserOnApp, setIsUserOnApp] = useState(false);
  const [prescriberData, setPrescriberData] = useState<Prescriber | null>(null);
  const [prescriberStatus, setPrescriberStatus] = useState<PRESCRIBER_STATUS | null>(null);
  const [userGroup, setUserGroup] = useState<USER_GROUPS>(
    USER_GROUPS[safeGetFromLocalStorage('userGroup')?.toUpperCase() as USER_GROUPS] ?? USER_GROUPS.not_validated
  );

  const { openToast, openBaseDialog, closeBaseDialog } = useFeedbackContext();
  const { setAssistantId, setAssistantData } = useAssistantContext();

  const location = useLocation();

  const [createPrescriberFormData, setCreatePrescriberFormData] = useState<CreatePrescriberPayload>(
    DEFAULT_CREATE_PRESCRIBER_FORM_DATA()
  );

  const { setSession } = useAuthContext();

  const [loadingPrescription, runWithloadingPrescription] = useLoading();
  const [prescriptionLink, setPrescriptionLink] = useState<string | null>(null);

  const [patients, setPatients] = useState<Patient[] | null>(null);
  const [bottomBarHeight, setBottomBarHeight] = useState(NOT_OPEN_BOTTOM_BAR_HEIGHT);

  const [userDoesNotHaveAnyPatients, setUserDoesNotHaveAnyPatients] = useState<boolean>(false);

  const updateCreatePrescriberFormData = useCallback((data: Partial<CreatePrescriberPayload>) => {
    setCreatePrescriberFormData((prevState) => {
      if (!prevState) return prevState;
      return {
        ...prevState,
        ...data,
      };
    });
  }, []);

  const startNewPrescription = useCallback(
    async (patientId: string) => {
      if (!prescriberId) return;
      try {
        const data = await PrescriberService.startPrescrition(prescriberId, patientId);
        setPrescriptionLink(data.modalUrl);
      } catch (error) {
        openToast('Erro ao iniciar prescrição. Tente novamente em instantes.', { type: 'error', duration: 2000 });
      }
    },
    [prescriberId, openToast]
  );

  const startPrescrition = useCallback(
    async (patientId: string) => {
      runWithloadingPrescription(async () => {
        if (!prescriberId) return;

        try {
          const previewData = await PrescriberService.getLatestPrescriptionPreview(prescriberId, patientId);

          // Se tiver dados, abre o modal de preview
          if (
            previewData &&
            (previewData.medicines?.length > 0 ||
              previewData.exams?.length > 0 ||
              previewData.referrals?.length > 0 ||
              previewData.medicalCertificates?.length > 0 ||
              previewData.reports?.length > 0)
          ) {
            await openBaseDialog(
              (dialogProps: DialogWrappedComponentProps) => (
                <PrescriptionPreviewModalWrapper
                  previewData={previewData}
                  prescriberId={prescriberId}
                  patientId={patientId}
                  onClose={dialogProps.onClose}
                  onNewPrescription={() => startNewPrescription(patientId)}
                  onResumePrescription={(modalUrl) => setPrescriptionLink(modalUrl)}
                />
              ),
              {
                width: '550px',
                maxWidth: '100vw',
                height: 'auto',
                maxHeight: '100vh',
                padding: '20px',
                borderRadius: '30px',
                overflow: 'hidden',
                motionPreset: 'none',
              }
            );
          } else {
            // Se não tiver dados no preview, abre direto nova prescrição
            await startNewPrescription(patientId);
          }
        } catch (error) {
          // Se der erro na API de preview, abre direto nova prescrição (comportamento atual)
          await startNewPrescription(patientId);
        }
      });
    },
    [prescriberId, runWithloadingPrescription, openBaseDialog, startNewPrescription]
  );

  const getAndSetPrescriberStatus = useCallback(async (prescriberId: string) => {
    const status = await PrescriberService.getPrescriberStatus(prescriberId);
    setPrescriberStatus(status);
  }, []);

  const handlePrescriberSession = useCallback(async () => {
    try {
      const cognitoUserData = await AuthService.getCurrentAuthenticatedUser();

      const userSession = await AuthService.getSession();
      const currUserGroup = get(userSession.getAccessToken(), "payload['cognito:groups'][0]", '');

      if (!cognitoUserData) return;

      setAuthorizationHeader(userSession.getAccessToken().getJwtToken());

      setSession(userSession);
      const cognitoUserId = cognitoUserData.find((item) => item.Name === 'custom:userId');
      if (!cognitoUserId) return;

      setPrescriberId(cognitoUserId.Value);

      let userData: AssistantData | Prescriber | null = null;

      setUserGroup(USER_GROUPS[currUserGroup as keyof typeof USER_GROUPS]);

      if (currUserGroup === USER_GROUPS.assistant) {
        setAssistantId(cognitoUserId.Value);
        userData = await AssistantService.getAssistant(cognitoUserId.Value);
        setAssistantData(userData);
      } else {
        userData = await PrescriberService.getPrescriber(cognitoUserId.Value);
        await getAndSetPrescriberStatus(cognitoUserId.Value);
        setPrescriberData(userData);
        const crm = encryptToMd5(userData.professionalRecord.number);

        Tracker.identify(crm);
        Tracker.register({
          CRM: crm,
        });
      }

      Tracker.trackEvent({
        event: 'Login',
        parameters: {
          Nome: userData.fullName,
          email: userData.email,
          'Data login': new Date(),
        },
      });

      const userGuiding = get(window, 'userGuiding', null) as any;

      if (userGuiding) {
        userGuiding.identify(userData._id, {
          name: userData.fullName,
          email: userData.email,
          partner: B2C_PARTNER_ID,
        });
      }
    } catch (error) {
      if (isUserOnApp) {
        appFailureSignedIn();
        Tracker.trackEvent({
          event: 'Erro - Abrir Webview',
          parameters: {
            data: error,
          },
        });
      }

      if (error === USER_DOES_NOT_HAVE_SESSION) {
        // a proteção de rotas irá deslogar o usuario
        return;
      }

      openToast('Algo deu errado. Tente novamente em instantes.', { type: 'error', duration: 2000 });
    }
  }, [getAndSetPrescriberStatus, isUserOnApp, openToast, setAssistantData, setAssistantId, setSession]);

  const retrievePrescriberData = useCallback(() => {
    return new Promise<void>(async (resolve, reject) => {
      return runWithLoadingPrescriberData(async () => {
        try {
          await handlePrescriberSession();

          resolve();
          return;
        } catch (error) {
          reject();
        }
      });
    });
  }, [handlePrescriberSession, runWithLoadingPrescriberData]);

  const clickOnFirstPatientFromList = useCallback(() => {
    const firstPatientId = get(patients, '[0]._id', '');

    if (!firstPatientId) return;

    const element = document.getElementById(`patient-card-${firstPatientId}`);
    if (!element) return;

    element.focus();
    element.click();
  }, [patients]);

  useEffect(() => {
    const run = () => {
      retrievePrescriberData();
    };

    run();
  }, [retrievePrescriberData]);

  useEffect(() => {
    setIsUserOnApp(isRunningInWebview());
  }, []);

  useEffect(() => {
    if (!location.pathname.startsWith('/inicio/receita')) {
      if (closeBaseDialog) {
        closeBaseDialog();
      }
      setPrescriptionLink(null);
    }
  }, [location, closeBaseDialog]);

  useEffect(() => {
    const run = async () => {
      if (!prescriptionLink) return;

      console.log('prescriptionLink', prescriptionLink);
      await openBaseDialog(
        (dialogProps: DialogWrappedComponentProps) => (
          <PrescriptionViewer
            onClose={() => {
              dialogProps.onClose();
              setPrescriptionLink(null);
            }}
            url={prescriptionLink}
            isUserOnApp={isUserOnApp}
          />
        ),
        {
          width: '100%',
          height: '100%',
          borderRadius: '0px',
          padding: '0px',
          persist: true,
          backgroundColor: 'transparent',
        }
      );
    };

    run();
  }, [prescriptionLink, openBaseDialog, isUserOnApp]);

  const isUserInvalid = useMemo(
    () =>
      prescriberStatus === PRESCRIBER_STATUS.INVALID ||
      prescriberStatus === PRESCRIBER_STATUS['ACTIVATION PENDING'] ||
      prescriberStatus === PRESCRIBER_STATUS.INACTIVE,
    [prescriberStatus]
  );

  return (
    <PrescriberContext.Provider
      value={{
        updateCreatePrescriberFormData,
        createPrescriberFormData,
        prescriberId,
        setPrescriberId,
        prescriberData,
        setPrescriberData,
        retrievePrescriberData,
        startPrescrition,
        loadingPrescription,
        patients,
        setPatients,
        loadingPrescriberData,
        isUserOnApp,
        bottomBarHeight,
        setBottomBarHeight,
        clickOnFirstPatientFromList,
        userDoesNotHaveAnyPatients,
        setUserDoesNotHaveAnyPatients,
        prescriberStatus,
        isUserInvalid,
        getAndSetPrescriberStatus,
        userGroup,
      }}
    >
      <>{props.children}</>
    </PrescriberContext.Provider>
  );
}

export function usePrescriberContext() {
  const prescriberContext = useContext(PrescriberContext);

  return prescriberContext;
}
